import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Spin,
  Alert,
  Table,
  Space,
  Empty,
  DatePicker,
} from 'antd';
import { Column } from '@ant-design/charts';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { getDashboard } from '../api/budget';

interface DashboardData {
  total_spend: number;
  total_income: number;
  total_savings: number;
  total_invested: number;
  monthly_history: Array<{
    month: string;
    spend: number;
  }>;
  top_categories: Array<{
    category: string;
    total: number;
  }>;
  credit_alerts: Array<{
    id: string;
    message: string;
    card_last4: string;
  }>;
}

type PeriodValue = 'this_month' | 'last_month' | 'last_3_months' | 'this_year';

interface PeriodOption {
  label: string;
  value: PeriodValue;
}

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<PeriodValue>('this_month');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);

  const periodOptions: PeriodOption[] = [
    { label: 'This Month', value: 'this_month' },
    { label: 'Last Month', value: 'last_month' },
    { label: 'Last 3 Months', value: 'last_3_months' },
    { label: 'This Year', value: 'this_year' },
  ];

  const getDateRange = (selectedPeriod: PeriodValue): { from: string; to: string } => {
    const today = dayjs();
    let from: Dayjs;
    let to: Dayjs;

    switch (selectedPeriod) {
      case 'this_month':
        from = today.startOf('month');
        to = today.endOf('month');
        break;
      case 'last_month':
        from = today.subtract(1, 'month').startOf('month');
        to = today.subtract(1, 'month').endOf('month');
        break;
      case 'last_3_months':
        from = today.subtract(3, 'months').startOf('month');
        to = today.endOf('month');
        break;
      case 'this_year':
        from = today.startOf('year');
        to = today.endOf('year');
        break;
      default:
        from = today.startOf('month');
        to = today.endOf('month');
    }

    return {
      from: from.format('YYYY-MM-DD'),
      to: to.format('YYYY-MM-DD'),
    };
  };

  const fetchDashboard = async (selectedPeriod: PeriodValue) => {
    setLoading(true);
    try {
      const range = getDateRange(selectedPeriod);
      const resp = await getDashboard({ from: range.from, to: range.to });
      if (resp.ok && resp.data) setData(resp.data as unknown as DashboardData);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard(period);
  }, [period]);

  const handlePeriodChange = (value: PeriodValue) => {
    setPeriod(value);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const chartData = data?.monthly_history.map((item) => ({
    month: item.month,
    spend: item.spend,
  })) || [];

  const categoriesTableColumns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: '70%',
    },
    {
      title: 'Total Spend',
      dataIndex: 'total',
      key: 'total',
      render: (total: number) => (
        <span style={{ color: '#f5222d', fontWeight: 'bold' }}>
          {formatCurrency(total)}
        </span>
      ),
      align: 'right' as const,
    },
  ];

  const netSavings = (data?.total_income || 0) - (data?.total_spend || 0);
  const netSavingsColor = netSavings >= 0 ? '#52c41a' : '#f5222d';

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Card style={{ marginBottom: '24px' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ margin: 0 }}>Budget Dashboard</h1>
            <Select
              style={{ width: '200px' }}
              value={period}
              onChange={handlePeriodChange}
              options={periodOptions}
            />
          </div>
        </Space>
      </Card>

      <Spin spinning={loading}>
        {data && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* KPI Cards */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Income"
                    value={data.total_income}
                    prefix="$"
                    valueStyle={{ color: '#52c41a' }}
                    formatter={(value) => formatCurrency(Number(value))}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Spend"
                    value={data.total_spend}
                    prefix="$"
                    valueStyle={{ color: '#f5222d' }}
                    formatter={(value) => formatCurrency(Number(value))}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Savings"
                    value={data.total_savings}
                    prefix="$"
                    valueStyle={{ color: '#1890ff' }}
                    formatter={(value) => formatCurrency(Number(value))}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Invested"
                    value={data.total_invested}
                    prefix="$"
                    valueStyle={{ color: '#722ed1' }}
                    formatter={(value) => formatCurrency(Number(value))}
                  />
                </Card>
              </Col>
            </Row>

            {/* Net Savings */}
            <Card>
              <Statistic
                title="Net Savings"
                value={netSavings}
                prefix={netSavings >= 0 ? '✓ $' : '- $'}
                valueStyle={{ color: netSavingsColor, fontSize: '24px', fontWeight: 'bold' }}
                formatter={(value) => formatCurrency(Math.abs(Number(value)))}
                suffix={netSavings >= 0 ? null : ''}
              />
              <p style={{ marginTop: '12px', color: '#666', fontSize: '12px' }}>
                {netSavings >= 0 ? 'You are' : 'You are'} {netSavings >= 0 ? 'saving' : 'spending over'} your income
              </p>
            </Card>

            {/* Monthly History Chart */}
            {chartData.length > 0 && (
              <Card title="Spending Trend (Last 6 Months)">
                <Column
                  data={chartData}
                  xField="month"
                  yField="spend"
                  seriesField="spend"
                  columnStyle={{
                    radius: [8, 8, 0, 0],
                  }}
                  color="#f5222d"
                  meta={{
                    spend: {
                      alias: 'Spend',
                      formatter: (v: number) => formatCurrency(v),
                    },
                  }}
                  tooltip={{
                    formatter: (datum: Record<string, number>) => ({
                      name: 'Spend',
                      value: formatCurrency(datum['spend'] ?? datum['total'] ?? 0),
                    }),
                  }}
                  height={300}
                />
              </Card>
            )}

            {/* Top Spending Categories */}
            {data.top_categories && data.top_categories.length > 0 && (
              <Card title="Top Spending Categories">
                <Table
                  columns={categoriesTableColumns}
                  dataSource={data.top_categories.map((cat, idx) => ({
                    key: idx,
                    ...cat,
                  }))}
                  pagination={false}
                  locale={{ emptyText: <Empty description="No spending data" /> }}
                />
              </Card>
            )}

            {/* Credit Card Alerts */}
            {data.credit_alerts && data.credit_alerts.length > 0 && (
              <Card title="Alerts">
                <Space direction="vertical" style={{ width: '100%' }}>
                  {data.credit_alerts.map((alert) => (
                    <Alert
                      key={alert.id}
                      message={`Card ending in ${alert.card_last4}`}
                      description={alert.message}
                      type="warning"
                      showIcon
                    />
                  ))}
                </Space>
              </Card>
            )}
          </Space>
        )}
      </Spin>
    </div>
  );
};

export default Dashboard;
